"use client";

import { SessionProvider, useSession } from "next-auth/react";
import { useEffect, useState } from "react";

import {
  beginAuthBootstrap,
  markAuthReady,
  setAuthToken,
} from "@/lib/auth-token";

/**
 * Bridges the NextAuth session to the framework-agnostic token accessor the
 * data layer reads (ADR-0005). The HTTP client and Socket.IO handshake call
 * into `auth-token.ts`; this component keeps that value in sync with the live
 * session so swapping the auth framework later touches only this file.
 *
 * It also owns the readiness gate. `useSession()` reports `status: "loading"`
 * until `/api/auth/session` comes back over the network, so on a cold page load
 * there is a real window in which no token exists. Requests made in that window
 * used to go out bare and 401 — every authenticated page failed on first paint
 * and worked on Retry. The gate closes before any page renders and opens once
 * the session settles, either way:
 *
 *   - `authenticated`   → token published, requests proceed with it
 *   - `unauthenticated` → no token, requests proceed and 401 honestly, which
 *                         `QueryBoundary` renders as "please log in"
 */
function AuthTokenSync() {
  const { data, status } = useSession();
  const token = data?.user?.token ?? null;
  const settled = status !== "loading";

  useEffect(() => {
    if (!settled) return;
    // Order matters: publish the token before releasing parked requests, or
    // they resume and read the old value.
    setAuthToken(token);
    markAuthReady();
  }, [settled, token]);

  return null;
}

/** Wraps the app in NextAuth's `SessionProvider` and the token bridge. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Close the gate during this first render — synchronously, before any child
  // page mounts and fires a query. An effect would be too late: TanStack Query
  // starts fetching from the children's mount effects, which run first.
  useState(beginAuthBootstrap);

  return (
    <SessionProvider>
      <AuthTokenSync />
      {children}
    </SessionProvider>
  );
}
